import * as PIXI from 'pixi.js';
import { MAP_WIDTH, MAP_HEIGHT, TILE_SIZE, NIGHT_LIGHTING_RADIUS, DAY_LIGHTING_RADIUS } from '../config.js';
import { Time } from '../world.js';
import { TICKS_PER_HOUR, TICKS_PER_DAY, DAY_START, DAY_END, NIGHT_START, NIGHT_END, DUSK_DURATION, DAWN_DURATION } from './time.js';
import { Position } from '../world.js';
import { Player } from '../world.js';

// Smooth easing function
function smoothStep(x) {
    return x * x * (3 - 2 * x);
}

export class LightingSystem {
    constructor() {
        // Initialize filters
        this.brightnessFilter = new PIXI.ColorMatrixFilter();
        this.desaturateFilter = new PIXI.ColorMatrixFilter();
        this.filters = [this.brightnessFilter, this.desaturateFilter];
        
        this.gradientTexture = null;
        this.gradientSprite = null;
        this.lastSunPosition = 0;
        this.currentRadius = DAY_LIGHTING_RADIUS;

        // Create canvas once and reuse it
        this.canvas = document.createElement('canvas');
        this.ctx = this.canvas.getContext('2d');
        this.size = Math.max(MAP_WIDTH, MAP_HEIGHT) * TILE_SIZE * 2;
        this.canvas.width = this.size;
        this.canvas.height = this.size;
        
        // Create radial gradient mask
        this.lightingMask = new PIXI.Sprite();
        this.lightingMask.anchor.set(0.5);
        this.lightingMask.visible = true;
        this.lightingMask.blendMode = 'multiply';
        
        // Temporarily skip gradient texture creation
        // this.updateGradientTexture(NIGHT_LIGHTING_RADIUS);
        
        console.log('LightingSystem constructor called with filters:', this.filters);
    }

    createGradientTexture(width, height) {
        console.log('Creating gradient texture:', { width, height });
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');

        // Create gradient with much smoother transitions
        const gradient = ctx.createLinearGradient(0, 0, width, 0);
        
        // Left side (darker)
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0.95)');     // Edge
        gradient.addColorStop(0.1, 'rgba(0, 0, 0, 0.9)');    // Start of transition
        gradient.addColorStop(0.2, 'rgba(0, 0, 0, 0.7)');    // Mid transition
        gradient.addColorStop(0.3, 'rgba(0, 0, 0, 0.4)');    // End of transition
        
        // Middle (clear)
        gradient.addColorStop(0.4, 'rgba(0, 0, 0, 0.2)');    // Start of clear
        gradient.addColorStop(0.5, 'rgba(0, 0, 0, 0)');      // Clear center
        gradient.addColorStop(0.6, 'rgba(0, 0, 0, 0.2)');    // End of clear
        
        // Right side (darker)
        gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.4)');    // Start of transition
        gradient.addColorStop(0.8, 'rgba(0, 0, 0, 0.7)');    // Mid transition
        gradient.addColorStop(0.9, 'rgba(0, 0, 0, 0.9)');    // End of transition
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0.95)');     // Edge

        // Fill with gradient
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);

        return PIXI.Texture.from(canvas);
    }

    updateGradientTexture(radius) {
        // Ensure radius is positive and within reasonable bounds
        radius = Math.max(1, Math.min(radius, this.size / 2));
        
        // Clear the canvas
        this.ctx.clearRect(0, 0, this.size, this.size);
        
        // Create radial gradient with current radius, centered in canvas
        const centerX = this.size / 2;
        const centerY = this.size / 2;
        const gradient = this.ctx.createRadialGradient(
            centerX, centerY, 0,           // Inner circle (center)
            centerX, centerY, radius  // Outer circle (edge)
        );
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');         // Center: transparent
        gradient.addColorStop(0.99, 'rgba(0, 0, 0, 0)');      // Just before edge: still transparent
        gradient.addColorStop(1, 'rgba(0, 0, 0, 1)');         // Edge: fully dark
        
        // Fill with gradient
        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(0, 0, this.size, this.size);
        
        // Destroy old texture if it exists
        if (this.lightingMask.texture) {
            this.lightingMask.texture.destroy(true);
        }
        
        // Create new texture and force a repaint
        this.lightingMask.texture = PIXI.Texture.from(this.canvas);
        this.lightingMask.texture.update();
        
        // Force the sprite to update its texture
        this.lightingMask._texture = this.lightingMask.texture;
        this.lightingMask._textureID = -1;
    }

    update(container, world) {
        const timeEntity = world.timeEntity;
        if (!timeEntity) return;

        // Get current time values
        const currentTick = Time.ticks[timeEntity];
        const isDay = Time.isDay[timeEntity] === 1;
        const baseBrightness = Time.brightness[timeEntity];
        
        // Calculate sun position (0-1) based on time of day
        // 0 = midnight, 0.5 = noon, 1 = midnight
        const sunPosition = (currentTick % TICKS_PER_DAY) / TICKS_PER_DAY;
        
        // Calculate lighting radius based on sun position
        let targetRadius = NIGHT_LIGHTING_RADIUS;
        let timeOfDay = '';
        
        // Calculate sun height (0-1) based on position
        // This creates a smooth sine wave for the sun's height
        const sunHeight = Math.sin(sunPosition * Math.PI * 2);
        
        // Calculate brightness based on sun height
        let brightness = 1.0;
        let desaturation = 0.0;
        
        if (sunHeight > 0.2) {
            // Day time - full brightness
            brightness = 1.0;
            desaturation = 0.0;
            targetRadius = DAY_LIGHTING_RADIUS;
            timeOfDay = 'day';
        } else if (sunHeight > 0) {
            // Dawn/Dusk transition
            const progress = (sunHeight - 0) / 0.2; // Normalize to 0-1
            const smooth = smoothStep(progress);
            brightness = 0.3 + (0.7 * smooth); // Brighten from 30% to 100%
            desaturation = 0.5 * (1 - smooth); // Reduce desaturation
            targetRadius = NIGHT_LIGHTING_RADIUS + (DAY_LIGHTING_RADIUS - NIGHT_LIGHTING_RADIUS) * smooth;
            timeOfDay = sunHeight > 0.1 ? 'dawn' : 'dusk';
        } else if (sunHeight > -0.2) {
            // Early night/late night transition
            const progress = (sunHeight + 0.2) / 0.4; // Normalize to 0-1
            const smooth = smoothStep(progress);
            brightness = 0.3 - (0.1 * smooth); // Darken from 30% to 20%
            desaturation = 0.5 + (0.2 * smooth); // Increase desaturation
            targetRadius = NIGHT_LIGHTING_RADIUS;
            timeOfDay = 'night';
        } else {
            // Deep night
            brightness = 0.2; // 20% brightness
            desaturation = 0.7; // 70% desaturation
            targetRadius = NIGHT_LIGHTING_RADIUS;
            timeOfDay = 'deep night';
        }

        // Smoothly interpolate current radius to target
        if (!this.currentRadius) this.currentRadius = targetRadius;
        this.currentRadius += (targetRadius - this.currentRadius) * 0.2;
        this.currentRadius = Math.max(1, this.currentRadius);

        // Get player position from world container
        const players = world.entities.filter(e => Player[e] === 1);
        if (players.length > 0) {
            const player = players[0];
            const playerSprite = world.sprites[player];
            if (playerSprite) {
                // Get the player's world position
                const worldX = Position.x[player];
                const worldY = Position.y[player];
                
                // Update mask position to follow player in world coordinates
                this.lightingMask.x = worldX;
                this.lightingMask.y = worldY;
            }
        }

        // Reset filters to defaults
        this.brightnessFilter.reset();
        this.desaturateFilter.reset();

        // Apply brightness and desaturation
        this.brightnessFilter.brightness(brightness);
        this.desaturateFilter.desaturate(desaturation);

        // Apply filters to container
        container.filters = this.filters;
    }

    applyToContainer(container) {
        if (!container) {
            console.error('Container is null or undefined');
            return;
        }
        
        // Apply filters
        container.filters = this.filters;
        
        console.log('Applying filters to container:', container.filters);

        // Add mask to container if not already added
        if (!this.lightingMask.parent) {
            container.addChild(this.lightingMask);
        }
        
        // Force a redraw
        //container.cacheAsBitmap = false; //->old
        container.updateCacheTexture(); //->new
    }

    destroy() {
        console.log('[LIGHTING] Destroying lighting system');
        // Destroy filters
        if (this.brightnessFilter) {
            this.brightnessFilter.destroy();
            this.brightnessFilter = null;
        }
        if (this.desaturateFilter) {
            this.desaturateFilter.destroy();
            this.desaturateFilter = null;
        }
        this.filters = null;

        // Destroy lighting mask
        if (this.lightingMask) {
            if (this.lightingMask.texture) {
                this.lightingMask.texture.destroy(true);
            }
            this.lightingMask.destroy();
            this.lightingMask = null;
        }

        // Clean up canvas
        if (this.canvas) {
            this.canvas.width = 0;
            this.canvas.height = 0;
            this.canvas = null;
        }
        this.ctx = null;

        console.log('[LIGHTING] Lighting system destroyed');
    }
} 